import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { User, Building2 } from 'lucide-react';

const BasicInfoStep = ({ formData, setFormData }) => {
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-3">
          <User className="h-6 w-6 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-1">ข้อมูลพื้นฐาน</h2>
        <p className="text-muted-foreground text-sm">กรอกข้อมูลพื้นฐานของโปรเจคและลูกค้า</p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Project Information */}
        <Card className="border-2 border-blue-200 hover:border-blue-300 transition-colors">
          <CardHeader className="bg-blue-50 py-3">
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <User className="h-5 w-5" />
              รายละเอียดโปรเจค
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="project_name" className="text-sm font-medium">
                ชื่อโปรเจค <span className="text-red-500">*</span>
              </Label>
              <Input
                id="project_name"
                placeholder="กรอกชื่อโปรเจค"
                value={formData.project_name}
                onChange={(e) => handleInputChange('project_name', e.target.value)}
                className="w-full h-10"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="client_name" className="text-sm font-medium">
                ชื่อลูกค้า <span className="text-red-500">*</span>
              </Label>
              <Input
                id="client_name"
                placeholder="กรอกชื่อลูกค้า"
                value={formData.client_name}
                onChange={(e) => handleInputChange('client_name', e.target.value)}
                className="w-full h-10"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="start_date" className="text-sm font-medium">
                วันที่เริ่มโปรเจค <span className="text-red-500">*</span>
              </Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date || ''}
                onChange={(e) => handleInputChange('start_date', e.target.value)}
                className="w-full h-10"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end_date" className="text-sm font-medium">
                วันที่จบโปรเจค
              </Label>
              <Input
                id="end_date"
                type="date"
                value={formData.end_date || ''}
                onChange={(e) => handleInputChange('end_date', e.target.value)}
                className="w-full h-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Sales Information */}
        <Card className="border-2 border-green-200 hover:border-green-300 transition-colors">
          <CardHeader className="bg-green-50 py-3">
            <CardTitle className="flex items-center gap-2 text-green-800">
              <Building2 className="h-5 w-5" />
              ข้อมูล Sale
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sales_name" className="text-sm font-medium">ชื่อ Sale</Label>
              <Input
                id="sales_name"
                placeholder="กรอกชื่อ Sale"
                value={formData.sales_name}
                onChange={(e) => handleInputChange('sales_name', e.target.value)}
                className="w-full h-10"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sales_contact" className="text-sm font-medium">เบอร์ติดต่อ Sale</Label>
              <Input
                id="sales_contact"
                placeholder="กรอกเบอร์ติดต่อ Sale"
                value={formData.sales_contact}
                onChange={(e) => handleInputChange('sales_contact', e.target.value)}
                className="w-full h-10"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="text-sm text-blue-600 font-medium">สถานะโปรเจค</div>
          <div className="text-lg font-bold text-blue-800">
            {formData.project_name ? 'พร้อมเริ่มต้น' : 'รอกรอกข้อมูล'}
          </div>
        </div>
        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
          <div className="text-sm text-green-600 font-medium">ข้อมูลลูกค้า</div>
          <div className="text-lg font-bold text-green-800">
            {formData.client_name ? 'ครบถ้วน' : 'ไม่ครบถ้วน'}
          </div>
        </div>
        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
          <div className="text-sm text-purple-600 font-medium">ข้อมูล Sale</div>
          <div className="text-lg font-bold text-purple-800">
            {formData.sales_name ? 'มีข้อมูล' : 'ไม่มีข้อมูล'}
          </div>
        </div>
        <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
          <div className="text-sm text-orange-600 font-medium">วันที่เริ่ม</div>
          <div className="text-lg font-bold text-orange-800">
            {formData.start_date ? new Date(formData.start_date).toLocaleDateString('th-TH') : 'ยังไม่ได้กำหนด'}
          </div>
        </div>
        <div className="p-4 bg-red-50 rounded-lg border border-red-200">
          <div className="text-sm text-red-600 font-medium">วันที่จบ</div>
          <div className="text-lg font-bold text-red-800">
            {formData.end_date ? new Date(formData.end_date).toLocaleDateString('th-TH') : 'ยังไม่ได้กำหนด'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicInfoStep;
